import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export type AiChangeDecorationKind = 'pending' | 'applied';

export interface AiChangeDecorationRange {
  from: number;
  to: number;
  kind?: AiChangeDecorationKind;
}

type DecorationMeta =
  | { ranges: AiChangeDecorationRange[] }
  | { clear: true };

export const aiChangeDecorationKey = new PluginKey<DecorationSet>('aiChangeDecoration');

export const AiChangeDecoration = Extension.create({
  name: 'aiChangeDecoration',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: aiChangeDecorationKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, set) {
            const meta = tr.getMeta(aiChangeDecorationKey) as DecorationMeta | undefined;

            if (meta && 'clear' in meta) {
              return DecorationSet.empty;
            }

            if (meta && 'ranges' in meta) {
              const decos = meta.ranges
                .filter((range) => range.from < range.to && range.to <= tr.doc.content.size)
                .map((range) =>
                  Decoration.inline(range.from, range.to, {
                    class:
                      (range.kind ?? 'applied') === 'pending'
                        ? 'ai-change-pending'
                        : 'ai-change-applied',
                  }),
                );
              return DecorationSet.create(tr.doc, decos);
            }

            return set.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return aiChangeDecorationKey.getState(state);
          },
        },
      }),
    ];
  },
});

export function setAiChangeDecorations(
  editor: Editor,
  ranges: AiChangeDecorationRange[],
): void {
  if (!editor || editor.isDestroyed || !editor.view) return;
  const tr = editor.state.tr.setMeta(aiChangeDecorationKey, {
    ranges,
  } satisfies DecorationMeta);
  editor.view.dispatch(tr);
}

export function clearAiChangeDecoration(editor: Editor): void {
  if (!editor || editor.isDestroyed || !editor.view) return;
  const tr = editor.state.tr.setMeta(aiChangeDecorationKey, { clear: true } satisfies DecorationMeta);
  editor.view.dispatch(tr);
}
